/**
 * Back/Forward Handler Hook
 *
 * Handles browser back/forward button navigation with scroll position restoration.
 * Integrates with history manager utilities for proper state management.
 *
 * Features:
 * - Listens to popstate events (back/forward navigation)
 * - Restores scroll position on navigation
 * - Saves scroll position before navigation
 * - Cleans up event listeners on unmount
 *
 * @module shared/hooks/routing/useBackForwardHandler
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  restoreScrollPosition,
  saveScrollPosition,
} from '@shared/utils/routing/historyManager';

/**
 * Hook to handle browser back/forward navigation with scroll position restoration
 *
 * Automatically saves scroll position before navigation and restores it
 * when user navigates back/forward using browser buttons.
 *
 * @example
 * ```typescript
 * function MyPage() {
 *   // Just use the hook - it handles everything automatically
 *   useBackForwardHandler();
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useBackForwardHandler() {
  const location = useLocation();

  useEffect(() => {
    /**
     * Handler for popstate events (back/forward navigation)
     * Restores scroll position when user navigates using browser buttons
     */
    const handlePopState = () => {
      // Restore scroll position from history state
      restoreScrollPosition();
    };

    // Add popstate event listener
    window.addEventListener('popstate', handlePopState);

    // Cleanup function: Save scroll position and remove listener
    return () => {
      // Save current scroll position before component unmounts or location changes
      saveScrollPosition();

      // Remove event listener
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]); // Re-run when location changes
}

/**
 * Extended back/forward handler with custom callbacks
 *
 * Allows custom handling of back/forward navigation events while
 * still maintaining automatic scroll position restoration.
 *
 * @param onBackward - Callback when user navigates backward
 * @param onForward - Callback when user navigates forward
 *
 * @example
 * ```typescript
 * function MyPage() {
 *   useBackForwardHandlerWithCallbacks(
 *     () => console.log('User went back'),
 *     () => console.log('User went forward')
 *   );
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useBackForwardHandlerWithCallbacks(
  onBackward?: () => void,
  onForward?: () => void
) {
  const location = useLocation();

  useEffect(() => {
    // Track current history state to detect direction
    let previousHistoryLength = window.history.length;

    const handlePopState = (event: PopStateEvent) => {
      // Restore scroll position
      restoreScrollPosition();

      // Detect navigation direction
      const currentHistoryLength = window.history.length;

      if (currentHistoryLength < previousHistoryLength) {
        // User went forward (history length decreased)
        onForward?.();
      } else {
        // User went backward (history length increased or stayed same)
        onBackward?.();
      }

      previousHistoryLength = currentHistoryLength;
    };

    window.addEventListener('popstate', handlePopState as EventListener);

    return () => {
      saveScrollPosition();
      window.removeEventListener('popstate', handlePopState as EventListener);
    };
  }, [location, onBackward, onForward]);
}
